'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, LinearProgress, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { clearCampaignWorkspace, getCampaignWorkspace, submitParticipantWork } from '../../redux/buzzbridge/actions';
import {
    campaignWorkspaceSelector,
    gettingCampaignWorkspaceSelector,
    participantUploadProgressSelector,
    participantWorkSubmitErrorSelector,
    submittingParticipantWorkSelector,
} from '../../redux/buzzbridge/selectors';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { showToastMessage } from '../../utils/AppUtils';
import { MetaTags } from '../../components/Metatags/Metatags';
import { CampaignParticipationGateBanner } from '../../components/BuzzBridge/CampaignParticipationGateBanner';
import { CampaignCapacityInfoBanner } from '../../components/BuzzBridge/CampaignCapacityInfoBanner';
import {
    isCreatorWorkspaceParticipationBlocked,
    resolveCampaignCapacity,
    resolveParticipationGate,
    shouldShowCampaignCapacityInfo,
} from '../../utils/campaignParticipationGate';
import CustomLazyLoadImage from '../../components/Image/CustomLazyLoadImage';

const BG = '#0e0e0e';
const SURFACE_LOW = '#131313';
const SURFACE = '#1a1919';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';

type SubmitDeliverable = {
    type: string;
    label: string;
    multiple_uploads?: { enabled: boolean; count: number; labels: string[] };
};

const FALLBACK_DELIVERABLE_TYPES: SubmitDeliverable[] = [
    { type: 'ugc_video', label: 'UGC Video' },
    { type: 'ugc_photo', label: 'UGC Photo' },
];
const MAX_BYTES = 500 * 1024 * 1024;

function rewardFromCampaign(c: { possible_earning?: unknown; budget?: unknown } & Record<string, unknown>): string {
    const raw = c.possible_earning ?? c.budget ?? c.reward;
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(raw);
    }
    if (typeof raw === 'string' && raw.trim()) {
        const n = parseFloat(raw.replace(/[^0-9.]/g, ''));
        if (!Number.isNaN(n)) {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
        }
        return raw;
    }
    return '—';
}

export default function CreatorSubmitWorkPage() {
    const { campaignId = '', participantId = '' } = useParams<{ campaignId: string; participantId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const workspace = useSelector(campaignWorkspaceSelector);
    const loadingWs = useSelector(gettingCampaignWorkspaceSelector);
    const submitting = useSelector(submittingParticipantWorkSelector);
    const uploadProgress = useSelector(participantUploadProgressSelector);
    const submitError = useSelector(participantWorkSubmitErrorSelector);

    const campaignDeliverables = useMemo((): SubmitDeliverable[] => {
        const raw = (workspace as any)?.campaign?.deliverables;
        if (Array.isArray(raw) && raw.length > 0 && raw[0]?.type) {
            return raw.map(
                (d: { type: string; label?: string; multiple_uploads?: SubmitDeliverable['multiple_uploads'] }) => ({
                    type: d.type,
                    label: d.label || d.type,
                    multiple_uploads: d.multiple_uploads,
                }),
            );
        }
        return FALLBACK_DELIVERABLE_TYPES;
    }, [workspace]);

    const [deliverableType, setDeliverableType] = useState<string>('');
    const [slotIndex, setSlotIndex] = useState<number>(0);
    const [files, setFiles] = useState<File[]>([]);
    const [description, setDescription] = useState('');
    const [externalLink, setExternalLink] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const activeDeliverable = useMemo(
        () => campaignDeliverables.find(d => d.type === deliverableType) ?? null,
        [campaignDeliverables, deliverableType],
    );
    const multiUpload = activeDeliverable?.multiple_uploads?.enabled ? activeDeliverable.multiple_uploads : null;

    const pid = participantId.trim();

    useEffect(() => {
        if (campaignDeliverables.length > 0 && !deliverableType) {
            setDeliverableType(campaignDeliverables[0].type);
            setSlotIndex(0);
        }
    }, [campaignDeliverables, deliverableType]);

    const participationGate = useMemo(() => resolveParticipationGate(workspace ?? undefined), [workspace]);
    const participationBlocked = useMemo(
        () => isCreatorWorkspaceParticipationBlocked(workspace ?? undefined),
        [workspace],
    );
    const campaignCapacity = useMemo(() => resolveCampaignCapacity(workspace ?? undefined), [workspace]);
    const showCapacityInfo = useMemo(() => shouldShowCampaignCapacityInfo(workspace ?? undefined), [workspace]);

    useEffect(() => {
        if (!pid) return;
        dispatch(
            getCampaignWorkspace({
                perspective: 'creator',
                participantId: pid,
                messagesPage: 1,
                messagesLimit: 5,
                appendMessages: false,
            }),
        );
        return () => {
            dispatch(clearCampaignWorkspace());
        };
    }, [dispatch, pid]);

    const workspacePath = useCallback(() => {
        let p = replaceRouteParam(ROUTES.BUZZ_BRIDGE_CAMPAIGN_WORKSPACE, 'campaignId', campaignId);
        p = replaceRouteParam(p, 'participantId', pid);
        return p;
    }, [campaignId, pid]);

    const goBack = useCallback(() => {
        navigate(workspacePath());
    }, [navigate, workspacePath]);

    const onFilesPicked = (list: FileList | null) => {
        if (!list?.length) return;
        const next: File[] = [];
        let big = false;
        Array.from(list).forEach(f => {
            if (f.size > MAX_BYTES) big = true;
            else next.push(f);
        });
        setLocalError(big ? 'Some files exceeded 500MB and were skipped.' : null);
        setFiles(prev => [...prev, ...next]);
    };

    const handleConfirm = () => {
        if (participationBlocked) return;
        setLocalError(null);
        if (!pid || !campaignId) {
            setLocalError('Missing campaign or participant.');
            return;
        }
        if (files.length === 0) {
            setLocalError('Select at least one file to upload.');
            return;
        }
        dispatch(
            submitParticipantWork({
                participantId: pid,
                campaignId,
                deliverableType,
                description: description.trim(),
                externalLink: externalLink.trim() || undefined,
                files,
                ...(multiUpload ? { slotIndex } : {}),
            }),
        );
    };

    const se = submitError as { data?: { message?: string }; message?: string } | undefined;
    const errMsg = localError || (se ? se.data?.message || se.message || 'Submission failed' : null);

    const campaign = workspace?.campaign;
    const brand = workspace?.brand;
    const title = campaign?.title ?? 'Campaign';
    const clientLine = brand?.name ?? 'Client';
    const end = campaign?.end_date;
    const dueLine =
        end != null
            ? (() => {
                  const d = new Date(end);
                  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
                  return days > 0 ? `Due in ${days} day${days === 1 ? '' : 's'}` : 'Past due';
              })()
            : '—';

    return (
        <MetaTags title="Submit work" description="Upload deliverables for your campaign">
            <Box sx={{ bgcolor: BG, color: '#fff', minHeight: '100vh', pb: 12 }}>
                <Box
                    component="header"
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        height: 64,
                        px: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: BG,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={goBack} sx={{ color: GOLD }} aria-label="Back">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontWeight: 800, fontSize: 18, color: GOLD }}>
                            Submit Work
                        </Typography>
                    </Box>
                    <Button
                        onClick={() =>
                            showToastMessage(
                                'Message the brand from your campaign workspace or contact support.',
                                'info',
                            )
                        }
                        sx={{ color: GOLD, fontFamily: FONT_ENUM.MAIN_BOLD, textTransform: 'none' }}>
                        Help
                    </Button>
                </Box>

                <Box
                    component="main"
                    sx={{ pt: 12, px: 3, maxWidth: 720, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {loadingWs && !workspace ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: GOLD }} />
                        </Box>
                    ) : null}

                    {participationGate && participationBlocked ? (
                        <CampaignParticipationGateBanner gate={participationGate} variant="creator" />
                    ) : null}

                    {showCapacityInfo ? <CampaignCapacityInfoBanner capacity={campaignCapacity} /> : null}

                    {workspace && campaign ? (
                        <Box
                            sx={{
                                display: 'grid',
                                opacity: participationBlocked ? 0.55 : 1,
                                pointerEvents: participationBlocked ? 'none' : 'auto',
                                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                gap: 2,
                            }}>
                            <Box sx={{ p: 3, bgcolor: SURFACE_LOW, borderRadius: 2 }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: MINT,
                                        letterSpacing: '0.2em',
                                    }}>
                                    ACTIVE PROJECT
                                </Typography>
                                <Typography
                                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontWeight: 900, fontSize: 24, mt: 1 }}>
                                    {title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                                    {(brand?.profile_picture || campaign.feature_image) && (
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                            }}>
                                            <CustomLazyLoadImage
                                                src={(brand?.profile_picture || campaign.feature_image) as string}
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </Box>
                                    )}
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                            Client: {clientLine}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: ON_VARIANT }}>{dueLine}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    p: 3,
                                    bgcolor: SURFACE,
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}>
                                <PaymentsOutlinedIcon sx={{ fontSize: 36, color: GOLD, mb: 1 }} />
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: ON_VARIANT,
                                        letterSpacing: '0.12em',
                                    }}>
                                    EARNINGS
                                </Typography>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, mt: 0.5 }}>
                                    {rewardFromCampaign(campaign as Record<string, unknown>)}
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}

                    <Box>
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: ON_VARIANT,
                                letterSpacing: '0.08em',
                                mb: 2,
                            }}>
                            DELIVERABLE TYPE
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {campaignDeliverables.map(d => (
                                <Button
                                    key={d.type}
                                    type="button"
                                    onClick={() => {
                                        setDeliverableType(d.type);
                                        setSlotIndex(0);
                                        setFiles([]);
                                    }}
                                    sx={{
                                        px: 2.5,
                                        py: 1.25,
                                        borderRadius: 999,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 14,
                                        textTransform: 'none',
                                        ...(deliverableType === d.type
                                            ? {
                                                  bgcolor: GOLD,
                                                  color: '#392700',
                                                  boxShadow: '0 8px 24px rgba(255,201,101,0.15)',
                                              }
                                            : { bgcolor: '#201f1f', color: ON_VARIANT }),
                                    }}>
                                    {d.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>

                    {/* Slot selection — only shown for multi-upload deliverables */}
                    {multiUpload && (
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: ON_VARIANT,
                                    letterSpacing: '0.08em',
                                    mb: 2,
                                }}>
                                SELECT VERSION
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {multiUpload.labels.map((label, i) => (
                                    <Button
                                        key={i}
                                        onClick={() => {
                                            setSlotIndex(i);
                                            setFiles([]);
                                        }}
                                        sx={{
                                            px: 2.5,
                                            py: 1.25,
                                            borderRadius: 999,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            textTransform: 'none',
                                            ...(slotIndex === i
                                                ? {
                                                      bgcolor: GOLD,
                                                      color: '#392700',
                                                      boxShadow: '0 8px 24px rgba(255,201,101,0.15)',
                                                  }
                                                : { bgcolor: '#201f1f', color: ON_VARIANT }),
                                        }}>
                                        {label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Box>
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: ON_VARIANT,
                                letterSpacing: '0.08em',
                                mb: 2,
                            }}>
                            UPLOAD CONTENT{multiUpload ? ` — ${multiUpload.labels[slotIndex] ?? ''}` : ''}
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            hidden
                            accept="video/mp4,video/quicktime,image/jpeg,image/png,image/webp"
                            onChange={e => {
                                onFilesPicked(e.target.files);
                                e.target.value = '';
                            }}
                        />
                        <Box
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => {
                                e.preventDefault();
                                onFilesPicked(e.dataTransfer.files);
                            }}
                            sx={{
                                border: '2px dashed rgba(72,72,71,0.35)',
                                bgcolor: SURFACE_LOW,
                                borderRadius: 2,
                                py: 6,
                                px: 3,
                                textAlign: 'center',
                                cursor: 'pointer',
                                '&:hover': { borderColor: 'rgba(255,201,101,0.45)', bgcolor: '#201f1f' },
                            }}
                            onClick={() => fileInputRef.current?.click()}>
                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,201,101,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2,
                                }}>
                                <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: GOLD }} />
                            </Box>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18 }}>
                                Drag &amp; drop files here
                            </Typography>
                            <Typography sx={{ color: ON_VARIANT, fontSize: 14, mt: 1 }}>
                                MP4, MOV, or JPG (Max 500MB)
                            </Typography>
                            <Button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                                sx={{
                                    mt: 2,
                                    px: 3,
                                    py: 1,
                                    bgcolor: SURFACE_HI,
                                    color: '#fff',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    borderRadius: 2,
                                    border: '1px solid rgba(72,72,71,0.2)',
                                    textTransform: 'none',
                                }}>
                                Select Files
                            </Button>
                            {files.length > 0 ? (
                                <Box sx={{ mt: 2, textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                                    {files.map(f => (
                                        <Typography key={f.name + f.size} sx={{ fontSize: 13, color: ON_VARIANT }}>
                                            {f.name} ({(f.size / (1024 * 1024)).toFixed(1)} MB)
                                        </Typography>
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: ON_VARIANT,
                                letterSpacing: '0.08em',
                                mb: 2,
                            }}>
                            DESCRIPTION / NOTES
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            placeholder="Provide any additional context or filming notes for the brand…"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: SURFACE_HI,
                                    borderRadius: 2,
                                    color: '#fff',
                                    '& fieldset': { border: 'none' },
                                    '&:hover fieldset': { border: 'none' },
                                    '&.Mui-focused fieldset': { border: '1px solid rgba(255,201,101,0.35)' },
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: ON_VARIANT,
                                    letterSpacing: '0.08em',
                                }}>
                                EXTERNAL LINK
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: ON_VARIANT }}>OPTIONAL</Typography>
                        </Box>
                        <Box sx={{ position: 'relative' }}>
                            <LinkIcon
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: ON_VARIANT,
                                }}
                            />
                            <TextField
                                fullWidth
                                type="url"
                                placeholder="Google Drive, Dropbox, or WeTransfer link"
                                value={externalLink}
                                onChange={e => setExternalLink(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: SURFACE_HI,
                                        borderRadius: 2,
                                        color: '#fff',
                                        pl: 5,
                                        '& fieldset': { border: 'none' },
                                        '&.Mui-focused fieldset': { border: '1px solid rgba(255,201,101,0.35)' },
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {errMsg ? <Alert severity="error">{errMsg}</Alert> : null}
                </Box>

                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        px: 3,
                        py: 2,
                        pb: 4,
                        bgcolor: 'rgba(19,19,19,0.85)',
                        backdropFilter: 'blur(12px)',
                    }}>
                    {submitting && uploadProgress > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography sx={{ fontSize: 12, color: ON_VARIANT }}>Uploading…</Typography>
                                <Typography sx={{ fontSize: 12, color: GOLD, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    {uploadProgress}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{
                                    borderRadius: 4,
                                    bgcolor: SURFACE_HI,
                                    '& .MuiLinearProgress-bar': { bgcolor: GOLD, borderRadius: 4 },
                                }}
                            />
                        </Box>
                    )}
                    <Button
                        fullWidth
                        disabled={submitting || !pid}
                        onClick={handleConfirm}
                        sx={{
                            py: 2,
                            borderRadius: 999,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 18,
                            textTransform: 'none',
                            background: `linear-gradient(90deg, ${GOLD} 0%, #feb700 100%)`,
                            color: '#392700',
                            boxShadow: '0 10px 30px rgba(254,183,0,0.2)',
                        }}>
                        {submitting ? <CircularProgress size={26} sx={{ color: '#392700' }} /> : 'Confirm Submission'}
                    </Button>
                </Box>
            </Box>
        </MetaTags>
    );
}
